/**
 * Router Dependencies
 */
import Vue from 'vue'
import Router from 'vue-router'
import store from '../state/store'
import ProjectRoutes from './projects'
import AccountRoutes from './account'
import WarRoomRoutes from './war-rooms'
import DashboardRoutes from './dashboard'
import PortfolioRoutes from './portfolios'
import ViewProjectRoutes from './view-project'
import DealPipelineRoutes from './deal-pipeline'

/**
 * Async Route Components
 */
const Login = r => require.ensure([], () => r(require('../components/views/Login')), 'guest')
const Dashboard = r => require.ensure([], () => r(require('../components/views/Dashboard')), 'dashboard')
const Register = r => require.ensure([], () => r(require('../components/views/Register')), 'guest')
const ResetPassword = r => require.ensure([], () => r(require('../components/views/ResetPassword')), 'guest')
const ViewProject = r => require.ensure([], () => r(require('../components/projects/views/ViewProject')), 'view-project')
const SharedProject = r => require.ensure([], () => r(require('../components/projects/shared/SharedProject')), 'view-project')
const SharedProjectIndex = r => require.ensure([], () => r(require('../components/projects/shared/SharedProjectIndex')), 'view-project')
const InviteRegistration = r => require.ensure([], () => r(require('../components/views/InviteRegistration')), 'guest')
const SharedProjectLogin = r => require.ensure([], () => r(require('../components/projects/views/SharedProjectLogin')), 'guest')
const CollaborationInvitation = r => require.ensure([], () => r(require('../components/views/CollaborationInvitation')), 'guest')
const ResetPasswordConfirmation = r => require.ensure([], () => r(require('../components/views/ResetPasswordConfirmation')), 'guest')
const ViewProjectInvitation = r => require.ensure([], () => r(require('../components/projects/views/ViewProjectInvitation')), 'guest')
const SharedProjectRegister = r => require.ensure([], () => r(require('../components/projects/views/SharedProjectRegister')), 'guest')
const SharedProjectChallenge = r => require.ensure([], () => r(require('../components/projects/views/SharedProjectChallenge')), 'guest')

/**
 * Debt Servicing Routes
 */
const DebtServicing = r => require.ensure([], () => r(require('../debt-servicing/DebtServicing')), 'debt-servicing')
const DebtServicingIndex = r => require.ensure([], () => r(require('../debt-servicing/DebtServicingIndex')), 'debt-servicing')

/**
 * Digital OM Components
 */
const DigitalOM = r => require.ensure([], () => r(require('../digital-om/components/DigitalOmBase.vue')), 'projects-show')

/**
 * Vue Plugins
 */
Vue.use(Router)

/**
 * Check the authentication status when entering the route
 *
 * @param  {Object}   to
 * @param  {Object}   from
 * @param  {Function} next
 * @return {Router}
 */
const checkAuth = (to, from, next) => {
    let loggedIn = store.state.user.isLoggedIn
    let token = window.localStorage.getItem('id_token')

    if (!loggedIn && token) {
        store.dispatch('user/checkAuthentication')
            .then(next)
            .catch(() => next({
                path: '/login',
                query: { redirect: to.fullPath }
            }))
    } else if (!loggedIn && !token) {
        next({
            path: '/login',
            query: { redirect: to.fullPath }
        })
    } else {
        next()
    }
}

/**
 * Check the auth status of an incoming user and validate
 * the token/authenticate the user if possible
 * while still allowing access to the route
 *
 * @param  {Function}   to
 * @param  {Function}   from
 * @param  {Function} next
 * @return {Router}
 */
const guestAuthCheck = (to, from, next) => {
    let loggedIn = store.state.user.isLoggedIn
    let token = window.localStorage.getItem('id_token')

    if (!loggedIn && token) {
        store.dispatch('user/checkAuthentication')
            .then(() => next())
            .catch(() => next())
    } else {
        next()
    }
}

/**
 * Redirect the user if they are already authenticated
 *
 * @return {[type]} [description]
 */
const redirectIfAuthenticated = (to, from, next) => {
    let loggedIn = store.state.user.isLoggedIn
    let token = window.localStorage.getItem('id_token')

    if (!loggedIn && token) {
        store.dispatch('user/checkAuthentication')
            .then(next('/'))
            .catch(() => next())
    } else {
        next()
    }
}

/**
 * Router definition
 *
 * @type {Router}
 */
const router = new Router({
    mode: 'history',
    base: __dirname,
    routes: [
        {
            path: '/login',
            name: 'login',
            component: Login,
            meta: { requiresAuth: false },
            beforeEnter: redirectIfAuthenticated
        },
        {
            path: '/reset-password',
            name: 'reset',
            component: ResetPassword,
            meta: { requiresAuth: false }
        },
        {
            path: '/account/password-reset',
            name: 'reset.form',
            component: ResetPasswordConfirmation,
            meta: { requiresAuth: false }
        },
        {
            path: '/projects/:projectId/digital-om',
            name: 'projects.show.dom',
            component: DigitalOM,
            meta: { requiresAuth: true },
            beforeEnter: checkAuth
        },
        {
            path: '/shared-projects/:projectKey',
            component: SharedProject,
            children: [
                {
                    path: '',
                    name: 'guest.shared',
                    component: SharedProjectIndex
                },
                {
                    path: 'authenticate',
                    name: 'guest.shared.login',
                    component: SharedProjectLogin
                },
                {
                    path: 'register',
                    name: 'guest.shared.register',
                    component: SharedProjectRegister
                },
                {
                    path: 'access',
                    name: 'guest.shared.challenge',
                    component: SharedProjectChallenge
                }
            ],
            beforeEnter: guestAuthCheck
        },
        {
            path: '/register',
            name: 'register',
            component: Register,
            beforeEnter: redirectIfAuthenticated
        },
        {
            path: '/register/:token/collaboration-invite',
            name: 'collaboration_invitation',
            component: CollaborationInvitation,
            beforeEnter: redirectIfAuthenticated
        },
        {
            path: '/auth/register/:token/company-user-register-invite',
            name: 'company_invite_registration',
            component: InviteRegistration,
            beforeEnter: redirectIfAuthenticated
        },
        {
            path: '/invitations/direct/:token',
            name: 'view_invitation',
            component: ViewProjectInvitation,
            beforeEnter: redirectIfAuthenticated
        },
        {
            path: '/',
            component: Dashboard,
            meta: { requiresAuth: true },
            children: [
                WarRoomRoutes,
                ProjectRoutes,
                AccountRoutes,
                PortfolioRoutes,
                DashboardRoutes,
                DealPipelineRoutes
            ],
            beforeEnter: checkAuth
        },
        {
            path: '/debt-servicing',
            component: DebtServicing,
            meta: { requiresAuth: true },
            children: [
                {
                    path: '',
                    name: 'debt_servicing',
                    component: DebtServicingIndex
                }
            ],
            beforeEnter: checkAuth
        },
        {
            path: '/view-project/:projectId',
            component: ViewProject,
            meta: { requiresAuth: true },
            children: ViewProjectRoutes,
            beforeEnter: checkAuth
        },
        { path: '*', redirect: '/' }
    ]
})

export default router



// WEBPACK FOOTER //
// ./src/router/index.js